import { TrendingUp, TrendingDown } from "lucide-react";
import clsx from "clsx";
import type { Candle } from "@/hooks/useCandles";
import { formatPrice, formatPercent, formatVolume } from "@/lib/format";

interface CandleStatsProps {
  candle: Candle | null;
  previousClose: number | null;
  source: string;
}

export function CandleStats({ candle, previousClose, source }: CandleStatsProps) {
  if (!candle) {
    return (
      <div className="flex h-8 items-center px-4 text-xs text-text-muted">
        No data
      </div>
    );
  }

  const change = previousClose !== null ? candle.close - previousClose : 0;
  const changePct = previousClose ? (change / previousClose) * 100 : 0;
  const isUp = change >= 0;
  const Trend = isUp ? TrendingUp : TrendingDown;

  return (
    <div className="flex h-9 items-center gap-5 border-b border-border bg-bg-panel px-4 text-xs tabular font-mono">
      <Field label="O" value={formatPrice(candle.open)} />
      <Field label="H" value={formatPrice(candle.high)} tone="bull" />
      <Field label="L" value={formatPrice(candle.low)} tone="bear" />
      <Field label="C" value={formatPrice(candle.close)} />
      <Field label="V" value={formatVolume(candle.volume)} tone="muted" />

      {previousClose !== null && (
        <div className={clsx("ml-auto flex items-center gap-1.5", isUp ? "text-bull" : "text-bear")}>
          <Trend className="h-3.5 w-3.5" />
          <span>{isUp ? "+" : ""}{formatPrice(change)}</span>
          <span className="opacity-70">({formatPercent(changePct)})</span>
        </div>
      )}

      <span
        className={clsx(
          "rounded-sm px-1.5 py-0.5 text-[10px] uppercase tracking-widest",
          source === "cache" ? "bg-bull/15 text-bull" : "bg-accent/15 text-accent"
        )}
        title={source === "cache" ? "Served from local cache" : "Fetched from upstream"}
      >
        {source}
      </span>
    </div>
  );
}

function Field({
  label,
  value,
  tone = "primary",
}: {
  label: string;
  value: string;
  tone?: "primary" | "bull" | "bear" | "muted";
}) {
  const toneClass = {
    primary: "text-text-primary",
    bull:    "text-bull",
    bear:    "text-bear",
    muted:   "text-text-secondary",
  }[tone];

  return (
    <div className="flex items-baseline gap-1.5">
      <span className="text-2xs uppercase tracking-wider text-text-muted">{label}</span>
      <span className={toneClass}>{value}</span>
    </div>
  );
}
